// html-text-splitter.ts
import { RecursiveCharacterTextSplitter } from "langchain/text_splitter";
import { JSDOM } from "jsdom";
import { A } from "@adornis/base/env-info.js";
function isHeader(node) {
  return node.level !== void 0 && node.text !== void 0;
}
function isAccordion(node) {
  return node.title !== void 0 && node.level !== void 0;
}
function hasHeaderAsChild(node) {
  if (!node)
    return false;
  for (let i = 0; i < node.childNodes.length; i++) {
    if (["H1", "H2", "H3", "H4", "H5", "H6"].some((h) => (node.childNodes[i]?.nodeName ?? "").endsWith(h)) || ["-ACCORDEON"].some((h) => (node.childNodes[i]?.nodeName ?? "").includes(h))) {
      return true;
    }
    if (hasHeaderAsChild(node.childNodes[i])) {
      return true;
    }
  }
  return false;
}
function hasNodeGridCellAsChild(node) {
  if (!node)
    return false;
  for (let i = 0; i < node.childNodes.length; i++) {
    if (node.childNodes[i]?.nodeName === "NODE-GRID-CELL") {
      return true;
    }
    if (hasNodeGridCellAsChild(node.childNodes[i])) {
      return true;
    }
  }
  return false;
}
function creatSpansAndFullText(fragments) {
  const spans = [];
  let fullText = "";
  for (let i = 0; i < fragments.length; i++) {
    const fragment = fragments[i];
    if (typeof fragment === "string") {
      fullText += fragment + " ";
    } else if (typeof fragment === "object") {
      const lastSpan = spans[spans.length - 1];
      if (lastSpan && lastSpan.end === -1) {
        if (isHeader(fragment)) {
          lastSpan.end = fullText.length;
          const lastHeader = lastSpan.meta.headers[lastSpan.meta.headers.length - 1];
          if (lastHeader && lastHeader.level < fragment.level) {
            spans.push({
              start: fullText.length,
              end: -1,
              meta: {
                headers: [...lastSpan.meta.headers, fragment],
                accordions: [...lastSpan.meta.accordions]
              }
            });
          } else {
            spans.push({
              start: fullText.length,
              end: -1,
              meta: {
                headers: [...lastSpan.meta.headers.filter((h) => h.level >= fragment.level), fragment],
                accordions: [...lastSpan.meta.accordions]
              }
            });
          }
        } else if (isAccordion(fragment)) {
          lastSpan.end = fullText.length;
          spans.push({
            start: fullText.length,
            end: -1,
            meta: {
              headers: [...lastSpan.meta.headers],
              accordions: [...lastSpan.meta.accordions.filter((h) => h.level < fragment.level), fragment]
            }
          });
        }
      } else {
        spans.push({
          start: fullText.length,
          end: -1,
          meta: {
            headers: isHeader(fragment) ? [fragment] : [],
            accordions: isAccordion(fragment) ? [fragment] : []
          }
        });
      }
    }
  }
  if (spans.length > 0 && spans[spans.length - 1]?.end === -1) {
    const last = spans[spans.length - 1];
    if (last) {
      last.end = fullText.length;
    }
  }
  return { fullText, spans };
}
async function chunkSpanBased(fullText, spans, chunkSize = 250, chunkOverlap = 30) {
  const textSplitter = new RecursiveCharacterTextSplitter({
    chunkSize,
    chunkOverlap
  });
  const rawChunks = await textSplitter.splitText(fullText);
  const chunks = [];
  for (let i = 0; i < rawChunks.length; i++) {
    const chunk = rawChunks[i];
    if (!chunk) {
      continue;
    }
    const start = fullText.indexOf(chunk);
    const end = start + chunk.length - 1;
    const newChunk = {
      text: chunk,
      meta: {}
    };
    for (let j = 0; j < spans.length; j++) {
      const span = spans[j];
      if (!span) {
        continue;
      }
      if (start >= span.start && start <= span.end || end >= span.start && end <= span.end) {
        span.meta.headers.forEach((header) => {
          newChunk.meta[`Heading ${header.level}`] = header.text;
        });
        span.meta.accordions.forEach((accordion) => {
          newChunk.meta[`Accordion ${accordion.level}: ${accordion.title}`] = "";
        });
      }
    }
    chunks.push(newChunk);
  }
  return chunks.map((c) => {
    c.meta["tags"] = [...new Set(c.text.match(/#(\w+)/g)?.map((t) => t.replace("#", "").toLowerCase()) ?? [])];
    return c;
  });
}
async function splitHtml(html, options) {
  const dom = A.isServer ? new JSDOM(html) : null;
  const doc = dom ? dom.window.document : new DOMParser().parseFromString(html, "text/html");
  const fragments = [];
  let accordionLevel = 0;
  function walk(node) {
    if (!node)
      return;
    const hTags = ["H1", "H2", "H3", "H4", "H5", "H6"];
    for (let i = 0; i < hTags.length; i++) {
      if (node.nodeName.includes(hTags[i] ?? "H1")) {
        fragments.push({ level: i + 1, text: (node.textContent ?? "").trim() });
        fragments.push("\n\n" + "#".repeat(i + 1) + " " + (node.textContent ?? "") + "\n\n");
        return;
      }
    }
    if (!hasHeaderAsChild(node) && !hasNodeGridCellAsChild(node)) {
      fragments.push(node.textContent ?? "");
      return;
    }
    if (node.nodeName === "NODE-ACCORDEON-CONTAINER") {
      accordionLevel++;
      const titleDiv = node.querySelector('[slot="accordeon-title"]');
      if (titleDiv) {
        fragments.push({
          title: titleDiv.textContent?.trim() || "",
          level: accordionLevel
        });
      }
    }
    if (node.nodeName === "NODE-GRID-CELL") {
      fragments.push("\n\n" + (node.textContent ?? "") + "\n\n");
      return;
    }
    for (let i = 0; i < node.childNodes.length; i++) {
      walk(node.childNodes[i]);
    }
    if (node.nodeName === "NODE-ACCORDEON-CONTAINER") {
      accordionLevel--;
    }
  }
  walk(doc.body);
  for (let i = 0; i < fragments.length - 1; i++) {
    const current = fragments[i];
    const next = fragments[i + 1];
    if (typeof current === "string" && typeof next === "string") {
      fragments[i] = current + " " + next;
      fragments.splice(i + 1, 1);
      i--;
    }
  }
  const { fullText, spans } = creatSpansAndFullText(
    fragments.filter((t) => {
      if (isAccordion(t)) {
        return t.title.length > 0;
      }
      if (isHeader(t)) {
        return t.text.length > 0;
      }
      return true;
    })
  );
  const chunks = chunkSpanBased(fullText, spans, options?.chunkSize, options?.chunkOverlap);
  return chunks;
}
export {
  splitHtml
};
